/**
 * PaymentsListView Organism
 * Pure presentation component for payments list with configurable grouping.
 * Supports three modes: flat (none), by pot reference, and by gang/solo.
 */

import { useCallback, useMemo } from 'react';
import { Box, Alert, Collapse } from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useShallow } from 'zustand/react/shallow';
import { PaymentListItem } from './PaymentListItem';
import { PaymentGroupHeader } from './PaymentGroupHeader';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';
import {
  groupPaymentsByPot,
  groupPaymentsByPotReference,
} from '../utils/groupPaymentsByPot';
import { aggregatePaymentsByWorker } from '../utils/groupPaymentsByWorker';
import { usePaymentsUIStore } from '../stores/paymentsUIStore';
import type { PaymentGroupingMode } from '../stores/paymentsUIStore';
import type { PaymentRowDto } from '../api/paymentsApi';

export interface PaymentsListViewProps {
  payments: PaymentRowDto[];
  isLoading: boolean;
  error: Error | null;
  showBankDetails: boolean;
  onPaymentClick?: (payment: PaymentRowDto) => void;
}

export function PaymentsListView({
  payments,
  isLoading,
  error,
  showBankDetails,
  onPaymentClick,
}: PaymentsListViewProps) {
  const { collapsedGroups, toggleGroup, groupingMode } = usePaymentsUIStore(
    useShallow(state => ({
      collapsedGroups: state.collapsedGroups,
      toggleGroup: state.toggleGroup,
      groupingMode: state.groupingMode,
    }))
  );

  const handleToggle = useCallback(
    (groupName: string) => () => toggleGroup(groupName),
    [toggleGroup]
  );

  if (error && !isLoading && payments.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load payments. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <ListViewShell
        itemCount={payments.length}
        loading={isLoading}
        emptyIcon={
          <PaymentsIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        }
        emptyTitle="No payments this week"
        emptyHelperText="Payment breakdowns appear here after pots are approved. Each worker's payout is shown individually."
        emptyMessage=""
        data-testid="payments-list"
      >
        {groupingMode === 'none' ? (
          <FlatPaymentsList
            payments={payments}
            showBankDetails={showBankDetails}
            onPaymentClick={onPaymentClick}
          />
        ) : (
          <GroupedPaymentsList
            payments={payments}
            groupingMode={groupingMode}
            collapsedGroups={collapsedGroups}
            showBankDetails={showBankDetails}
            onToggle={handleToggle}
            onPaymentClick={onPaymentClick}
          />
        )}
      </ListViewShell>
    </Box>
  );
}

function FlatPaymentsList({
  payments,
  showBankDetails,
  onPaymentClick,
}: {
  payments: PaymentRowDto[];
  showBankDetails: boolean;
  onPaymentClick?: (payment: PaymentRowDto) => void;
}) {
  const aggregated = useMemo(
    () => aggregatePaymentsByWorker(payments),
    [payments]
  );

  return (
    <>
      {aggregated.map((payment, index) => (
        <PaymentListItem
          key={`${payment.personName}-${payment.accountNumber}-${index}`}
          payment={payment}
          showBankDetails={showBankDetails}
          onClick={() => onPaymentClick?.(payment)}
          data-testid={`payment-row-${index}`}
        />
      ))}
    </>
  );
}

function GroupedPaymentsList({
  payments,
  groupingMode,
  collapsedGroups,
  showBankDetails,
  onToggle,
  onPaymentClick,
}: {
  payments: PaymentRowDto[];
  groupingMode: Exclude<PaymentGroupingMode, 'none'>;
  collapsedGroups: Set<string>;
  showBankDetails: boolean;
  onToggle: (groupName: string) => () => void;
  onPaymentClick?: (payment: PaymentRowDto) => void;
}) {
  const groups = useMemo(
    () =>
      groupingMode === 'pot'
        ? groupPaymentsByPotReference(payments)
        : groupPaymentsByPot(payments),
    [payments, groupingMode]
  );

  return (
    <>
      {groups.map((group, index) => {
        const isCollapsed = collapsedGroups.has(group.groupName);
        const panelId = `payment-group-panel-${index}`;
        return (
          <Box key={group.groupName}>
            <PaymentGroupHeader
              groupName={group.groupName}
              isGang={group.isGang}
              count={group.payments.length}
              totalAmount={group.totalAmount}
              expanded={!isCollapsed}
              onToggle={onToggle(group.groupName)}
              panelId={panelId}
              data-testid={`payment-group-${index}`}
            />
            <Collapse in={!isCollapsed} unmountOnExit>
              <Box id={panelId}>
                {group.payments.map((payment, index) => (
                  <PaymentListItem
                    key={`${payment.potReference}-${payment.personName}-${index}`}
                    payment={payment}
                    showBankDetails={showBankDetails}
                    onClick={() => onPaymentClick?.(payment)}
                    data-testid={`payment-row-${index}`}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </>
  );
}
